import { useCustom, type HttpError } from "@refinedev/core";
import type { RelationshipGridColumnsResult } from "../types";

interface UseRelationshipGridColumnsOptions {
  relationshipName: string;
  enabled?: boolean;
}

interface UseRelationshipGridColumnsResult {
  data: RelationshipGridColumnsResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
  refetch: () => void;
}

/**
 * Hook to fetch grid column definitions for a RelationshipType.
 * Columns are fetched from the API which merges properties from both
 * the relationship ItemType and the related ItemType based on grid_view setting.
 */
export const useRelationshipGridColumns = ({
  relationshipName,
  enabled = true,
}: UseRelationshipGridColumnsOptions): UseRelationshipGridColumnsResult => {
  const {
    data: responseData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCustom<RelationshipGridColumnsResult>({
    url: `/meta/relationships/${encodeURIComponent(relationshipName)}/grid-columns`,
    method: "get",
    queryOptions: {
      enabled: enabled && !!relationshipName,
      staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    },
  });

  // Extract data from Refine's CustomResponse wrapper
  const data = responseData?.data as RelationshipGridColumnsResult | undefined;

  return {
    data,
    isLoading,
    isError,
    error: error || null,
    refetch,
  };
};
